import Load from 'components/Load';
import {
  NavigationalLayoutElement,
  type NavigationalLayoutProps,
} from 'modules/app/components/NavigationalLayout';
import Notfound from 'modules/errorpages/Notfound';
import AgendaBottomBar from 'modules/host-agenda/layout/AgendaBottomBar';
import { createHostGuardLoader } from 'modules/host/hostRouteLoader';
import React from 'react';
import {
  type LoaderFunction,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { relativePathFromBase } from 'utils/relativePathFromBase';

const AsyncOnboarding = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-onboarding" */ 'modules/host-agenda/pages/OnboardingOverview'
    ),
);

const AsyncMonthView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/MonthOverview'
    ),
);
const AsyncWeekView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/WeekOverview'
    ),
);
const AsyncDayView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/DayOverview'
    ),
);

const AsyncCreateEvent = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-create-event" */ 'modules/host-agenda/pages/CreateEventForm'
    ),
);
const AsyncUpdateEvent = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-update-event" */ 'modules/host-agenda/pages/UpdateEventForm'
    ),
);
const AsyncAgendaEventNotFound = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-not-found" */ 'modules/host-agenda/pages/AgendaEventNotFound'
    ),
);
const AsyncAgendaEventNoAccess = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-not-found" */ 'modules/host-agenda/pages/AgendaEventNoAccess'
    ),
);

const AsyncAgendaEventCreated = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventCreated'
    ),
);
const AsyncAgendaEventUpdated = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventUpdated'
    ),
);
const AsyncAgendaEventRemoved = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventRemoved'
    ),
);

const AsyncExceptionSettings = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-exceptions" */ 'modules/host-agenda/pages/BookAheadTimeException'
    ),
);
const AsyncBookAheadTimePage = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-exceptions" */ 'modules/host-agenda/pages/BookAheadTimeOverview'
    ),
);

const AsyncSettingsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/SettingsOverview'
    ),
);

const hostAgendaBase = `${basePath}/host-agenda`;
const hostGuardLoader = createHostGuardLoader();
type ChildRouteOptions = Omit<NavigationalLayoutProps, 'component'> & {
  path?: string;
  index?: boolean;
  loader?: LoaderFunction;
};

const LegacySettingsExceptionRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`${hostAgendaBase}/settings/book-ahead-time/exception/${id ?? ''}`}
      replace
    />
  );
};

const createChildRoute = (
  descriptor: RouteDescriptor,
  Component: React.ComponentType<any>,
  options: ChildRouteOptions = {},
) => {
  const {
    path: override,
    index: forcedIndex,
    loader,
    ...layoutProps
  } = options;
  const relative =
    override ?? relativePathFromBase(hostAgendaBase, descriptor.path);
  const isIndex = forcedIndex ?? relative === '';

  const element = (
    <NavigationalLayoutElement {...layoutProps} component={Component as any} />
  );

  if (isIndex) {
    return (
      <Route
        key={`${descriptor.id}__index`}
        id={descriptor.id}
        index
        handle={createRouteHandle(descriptor)}
        loader={loader}
        element={element}
      />
    );
  }

  return (
    <Route
      key={descriptor.id}
      id={descriptor.id}
      path={relative}
      handle={createRouteHandle(descriptor)}
      loader={loader}
      element={element}
    />
  );
};

export const hostAgendaRootRoute: RouteDescriptor = {
  id: 'host-agenda.root',
  path: `${hostAgendaBase}/*`,
  label: 'Host Agenda Root',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaOnboardingRoute: RouteDescriptor = {
  id: 'host-agenda.onboarding',
  path: `${hostAgendaBase}/onboarding`,
  label: 'Host Agenda Onboarding',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaOnboardingSlugRoute: RouteDescriptor = {
  id: 'host-agenda.onboarding.slug',
  path: `${hostAgendaBase}/onboarding/:slug`,
  label: 'Host Agenda Onboarding Step',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaCreateEventRoute: RouteDescriptor = {
  id: 'host-agenda.create-event',
  path: `${hostAgendaBase}/create-event/:start?/:end?`,
  label: 'Create Agenda Event',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaUpdateEventRoute: RouteDescriptor = {
  id: 'host-agenda.update-event',
  path: `${hostAgendaBase}/update-event/:id`,
  label: 'Update Agenda Event',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaEventNotFoundRoute: RouteDescriptor = {
  id: 'host-agenda.event-not-found',
  path: `${hostAgendaBase}/event-not-found`,
  label: 'Agenda Event Not Found',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaEventNoAccessRoute: RouteDescriptor = {
  id: 'host-agenda.event-no-access',
  path: `${hostAgendaBase}/no-access/`,
  label: 'Agenda Event No Access',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaEventCreatedRoute: RouteDescriptor = {
  id: 'host-agenda.event-created',
  path: `${hostAgendaBase}/event-created`,
  label: 'Agenda Event Created',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaEventUpdatedRoute: RouteDescriptor = {
  id: 'host-agenda.event-updated',
  path: `${hostAgendaBase}/event-updated`,
  label: 'Agenda Event Updated',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaEventRemovedRoute: RouteDescriptor = {
  id: 'host-agenda.event-removed',
  path: `${hostAgendaBase}/event-removed`,
  label: 'Agenda Event Removed',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaMonthRoute: RouteDescriptor = {
  id: 'host-agenda.month',
  path: `${hostAgendaBase}/month`,
  label: 'Agenda Month View',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaWeekRoute: RouteDescriptor = {
  id: 'host-agenda.week',
  path: `${hostAgendaBase}/week/:year/:weekNumber`,
  label: 'Agenda Week View',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaDayRoute: RouteDescriptor = {
  id: 'host-agenda.day',
  path: `${hostAgendaBase}/day/:date`,
  label: 'Agenda Day View',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaLegacySettingsExceptionRoute: RouteDescriptor = {
  id: 'host-agenda.settings.exception.legacy',
  path: `${hostAgendaBase}/settings/exception/:id`,
  label: 'Agenda Legacy Exception Redirect',
  audience: 'host',
  layout: 'standalone',
  module: 'host-agenda',
};

export const hostAgendaSettingsExceptionRoute: RouteDescriptor = {
  id: 'host-agenda.settings.exception',
  path: `${hostAgendaBase}/settings/book-ahead-time/exception/:id`,
  label: 'Agenda Exception Settings',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaBookAheadRoute: RouteDescriptor = {
  id: 'host-agenda.settings.book-ahead-time',
  path: `${hostAgendaBase}/settings/book-ahead-time`,
  label: 'Agenda Book Ahead Time',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaSettingsRoute: RouteDescriptor = {
  id: 'host-agenda.settings',
  path: `${hostAgendaBase}/settings`,
  label: 'Agenda Settings',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

export const hostAgendaHomeRoute: RouteDescriptor = {
  id: 'host-agenda.home',
  path: `${hostAgendaBase}/`,
  label: 'Agenda Home',
  audience: 'host',
  layout: 'navigational',
  module: 'host-agenda',
};

const SubRoutes = () => {
  return (
    <Routes>
      {createChildRoute(hostAgendaOnboardingRoute, AsyncOnboarding, {
        hideFooter: true,
        noNavbar: true,
        showBottomNavigation: true,
        bottomBar: <AgendaBottomBar />,
      })}
      {createChildRoute(hostAgendaOnboardingSlugRoute, AsyncOnboarding, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaCreateEventRoute, AsyncCreateEvent, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaUpdateEventRoute, AsyncUpdateEvent, {
        path: 'update-event/:id/*',
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(
        hostAgendaEventNotFoundRoute,
        AsyncAgendaEventNotFound,
        {
          hideFooter: true,
          noNavbar: true,
        },
      )}
      {createChildRoute(
        hostAgendaEventNoAccessRoute,
        AsyncAgendaEventNoAccess,
        {
          hideFooter: true,
          noNavbar: true,
        },
      )}
      {createChildRoute(hostAgendaEventCreatedRoute, AsyncAgendaEventCreated, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaEventUpdatedRoute, AsyncAgendaEventUpdated, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaEventRemovedRoute, AsyncAgendaEventRemoved, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaMonthRoute, AsyncMonthView, {
        hideFooter: true,
        noNavbar: true,
        showBottomNavigation: true,
        bottomBar: <AgendaBottomBar />,
      })}
      {createChildRoute(hostAgendaWeekRoute, AsyncWeekView, {
        hideFooter: true,
        noNavbar: true,
        showBottomNavigation: true,
        bottomBar: <AgendaBottomBar />,
      })}
      {createChildRoute(hostAgendaDayRoute, AsyncDayView, {
        hideFooter: true,
        noNavbar: true,
      })}
      <Route
        key={hostAgendaLegacySettingsExceptionRoute.id}
        path={hostAgendaLegacySettingsExceptionRoute.path}
        element={<LegacySettingsExceptionRedirect />}
      />
      {createChildRoute(
        hostAgendaSettingsExceptionRoute,
        AsyncExceptionSettings,
        {
          hideFooter: true,
          noNavbar: true,
        },
      )}
      {createChildRoute(hostAgendaBookAheadRoute, AsyncBookAheadTimePage, {
        hideFooter: true,
        noNavbar: true,
      })}
      {createChildRoute(hostAgendaSettingsRoute, AsyncSettingsPage, {
        hideFooter: true,
        noNavbar: true,
        showBottomNavigation: true,
        bottomBar: <AgendaBottomBar />,
      })}
      {createChildRoute(hostAgendaHomeRoute, AsyncMonthView, {
        hideFooter: true,
        noNavbar: true,
        showBottomNavigation: true,
        bottomBar: <AgendaBottomBar />,
        index: true,
      })}
      <Route path="*" element={<Notfound />} />
    </Routes>
  );
};

const HostAgendaRoutes = () => <SubRoutes />;

export const hostAgendaRoutes = [
  <Route
    key={hostAgendaRootRoute.id}
    id={hostAgendaRootRoute.id}
    path={hostAgendaRootRoute.path}
    handle={createRouteHandle(hostAgendaRootRoute)}
    loader={hostGuardLoader}
    element={<HostAgendaRoutes />}
  />,
];

export const hostAgendaRouteDescriptors = [
  hostAgendaRootRoute,
  hostAgendaOnboardingRoute,
  hostAgendaOnboardingSlugRoute,
  hostAgendaCreateEventRoute,
  hostAgendaUpdateEventRoute,
  hostAgendaEventNotFoundRoute,
  hostAgendaEventNoAccessRoute,
  hostAgendaEventCreatedRoute,
  hostAgendaEventUpdatedRoute,
  hostAgendaEventRemovedRoute,
  hostAgendaMonthRoute,
  hostAgendaWeekRoute,
  hostAgendaDayRoute,
  hostAgendaSettingsExceptionRoute,
  hostAgendaBookAheadRoute,
  hostAgendaSettingsRoute,
  hostAgendaHomeRoute,
];

export default hostAgendaRoutes;
